import { apiClient } from '@/api/api'
import type { SurveyConfig } from '../types/survey-config'
import type { SurveyResults } from '../store/useSurveyModalStore'
import { mapCategoryImage } from '@/utils/category-image-mapper'

export interface SurveySubmitRequest {
  survey_config_id: string
  answers: Record<string, any>
  metadata?: {
    completed_at?: string
    device_type?: string
    language?: string
  }
}

// Note: Response is transformed from snake_case to camelCase by caseTransformerInterceptor
export interface SurveySubmitResponse {
  surveyResponseId: number
}

export interface FetchSurveyConfigParams {
  config_id?: string
}

export async function fetchSurveyConfig(params?: FetchSurveyConfigParams): Promise<SurveyConfig> {
  const configId = params?.config_id || 'default-size-survey'
  const response = await apiClient.get<{ config: any }>('/survey/config', {
    params: { config_id: configId }
  })

  // Преобразуем данные из бэкенда (snake_case) в формат фронтенда (camelCase)
  const config = response.data.config
  if (config.steps) {
    config.steps = config.steps.map((step: any) => {
      if (step.type === 'measurement' && step.measurement_type) {
        // Преобразуем measurement_type в measurementType
        step.measurementType = step.measurement_type
        // Удаляем старое поле, чтобы избежать путаницы
        delete step.measurement_type
      }
      if (step.hint && step.hint.video_src !== undefined) {
        // Преобразуем video_src в video_src (оставляем как есть, так как мы обновили типы)
        // Но убеждаемся, что поле есть
        step.hint.video_src = step.hint.video_src
      }
      return step
    })
  }

  return config as SurveyConfig
}

export async function submitSurveyAnswers(data: SurveySubmitRequest): Promise<SurveySubmitResponse> {
  const response = await apiClient.post<SurveySubmitResponse>('/survey/submit', data)
  return response.data
}

export interface FetchSurveyResultsParams {
  surveyResponseId: number
  brand?: string  // 'all' | 'zara' | 'hm' | 'uniqlo' | 'nike' | и т.д.
  system?: string // 'metric' | 'us' | 'eu'
}

export async function fetchSurveyResults(params: FetchSurveyResultsParams): Promise<SurveyResults> {
  const { surveyResponseId, brand = 'all', system = 'metric' } = params
  // Бэкенд ожидает configId в camelCase для query параметров
  const requestParams = {
    configId: surveyResponseId.toString(),
    brand,
    system
  }
  const response = await apiClient.get<{ results: SurveyResults }>('/survey/results', {
    params: requestParams
  })
  // Бэкенд возвращает { results: {...} }, нужно извлечь results
  const results = response.data.results
  
  // Применяем маппер для изображений категорий
  if (results.tops?.image) {
    results.tops.image = mapCategoryImage(results.tops.image)
  }
  if (results.bottoms?.image) {
    results.bottoms.image = mapCategoryImage(results.bottoms.image)
  }
  if (results.dresses?.image) {
    results.dresses.image = mapCategoryImage(results.dresses.image)
  }
  if (results.outerwear?.image) {
    results.outerwear.image = mapCategoryImage(results.outerwear.image)
  }
  if (results.lingerie?.image) {
    results.lingerie.image = mapCategoryImage(results.lingerie.image)
  }
  if (results.shoes?.image) {
    results.shoes.image = mapCategoryImage(results.shoes.image)
  }
  
  return results
}

